import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTheme as useMuiTheme } from '@mui/material/styles';
import { triggerBrowserDownload } from '../utils/download';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Typography,
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Avatar,
  Grid,
  Card,
  CardContent,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  AppBar,
  Toolbar,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Pagination,
  LinearProgress
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  Edit as EditIcon,
  AccountCircle as AccountCircleIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  CloudUpload as CloudUploadIcon,
  BarChart as BarChartIcon,
  Storage as StorageIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  RestoreFromTrash as RestoreFromTrashIcon,
  DeleteForever as DeleteForeverIcon,
  FileOpen as FileOpenIcon,
  PictureAsPdf as PictureAsPdfIcon,
  Description as DescriptionIcon,
  YouTube as YouTubeIcon,
  InsertDriveFile as InsertDriveFileIcon,
  HelpOutline as HelpOutlineIcon,
  Refresh as RefreshIcon,
  Image as ImageIcon,
  VideoFile as VideoFileIcon,
  AudioFile as AudioFileIcon,
  Archive as ArchiveIcon,
  TableChart as TableChartIcon,
  TextFields as TextFieldsIcon,
  Code as CodeIcon,
  TableChart as SpreadsheetIcon,
  Send as SendIcon,
  Psychology as PsychologyIcon,
  LibraryBooks as LibraryBooksIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import axios from 'axios';
import { APP_NAME } from '../constants';
import RajidaLogo from '../imgs/Rajida-New-Logo.png';
import { PieChart, Pie, Cell } from 'recharts';

const DRAWER_WIDTH = 280;
const DRAWER_COLLAPSED_WIDTH = 64;

const scrollText = keyframes`
  0% { transform: translateX(100%); }
  100% { transform: translateX(-100%); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

function formatSize(size) {
  if (size < 1024) return size + ' B';
  if (size < 1024 * 1024) return (size / 1024).toFixed(1) + ' KB';
  if (size < 1024 * 1024 * 1024) return (size / (1024 * 1024)).toFixed(1) + ' MB';
  return (size / (1024 * 1024 * 1024)).toFixed(1) + ' GB';
}

function truncateMiddle(str, maxLength = 70) {
  if (str.length <= maxLength) return str;
  const keep = Math.floor((maxLength - 3) / 2);
  return str.slice(0, keep) + '...' + str.slice(-keep);
}

function getFileIcon(fileName) {
  const extension = fileName.split('.').pop()?.toLowerCase();
  
  switch (extension) {
    // Images
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'bmp':
    case 'svg':
    case 'webp':
      return { icon: ImageIcon, color: 'success.main' };
    
    // Videos
    case 'mp4':
    case 'avi':
    case 'mov':
    case 'wmv':
    case 'flv':
    case 'webm':
    case 'mkv':
      return { icon: VideoFileIcon, color: 'error.main' };
    
    // Audio
    case 'mp3':
    case 'wav':
    case 'flac':
    case 'aac':
    case 'ogg':
    case 'wma':
      return { icon: AudioFileIcon, color: 'warning.main' };
    
    // Archives
    case 'zip':
    case 'rar':
    case '7z':
    case 'tar':
    case 'gz':
    case 'bz2':
      return { icon: ArchiveIcon, color: 'info.main' };
    
    // Documents
    case 'pdf':
      return { icon: PictureAsPdfIcon, color: 'error.main' };
    
    case 'doc':
    case 'docx':
      return { icon: DescriptionIcon, color: 'primary.main' };
    
    case 'xls':
    case 'xlsx':
    case 'csv':
      return { icon: SpreadsheetIcon, color: 'success.main' };
    
    case 'txt':
    case 'rtf':
      return { icon: TextFieldsIcon, color: 'text.secondary' };
    
    // Code files
    case 'js':
    case 'ts':
    case 'jsx':
    case 'tsx':
    case 'html':
    case 'css':
    case 'scss':
    case 'sass':
    case 'json':
    case 'xml':
    case 'py':
    case 'java':
    case 'cpp':
    case 'c':
    case 'php':
    case 'sql':
    case 'sh':
    case 'bat':
      return { icon: CodeIcon, color: 'warning.main' };
    
    // Data files
    case 'db':
    case 'sqlite':
    case 'mdb':
      return { icon: TableChartIcon, color: 'info.main' };
    
    // Default
    default:
      return { icon: InsertDriveFileIcon, color: 'grey.500' };
  }
}

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, changePassword } = useAuth();
  const token = localStorage.getItem('token');
  const { mode, toggleTheme } = useTheme();
  const muiTheme = useMuiTheme();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('drive'); // 'profile', 'activities', 'drive', 'ai-chat', 'resources'
  const [activities, setActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploadError, setUploadError] = useState('');
  const [trashFiles, setTrashFiles] = useState([]);
  const [sqlBackups, setSqlBackups] = useState([]);
  const [sqlBackupsLoading, setSqlBackupsLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [fileToDownload, setFileToDownload] = useState(null);
  const [downloadType, setDownloadType] = useState(''); // 'file', 'reference', 'sqlbackup'
  // Set drawerOpen to false and remove toggleDrawer logic and MenuIcon button
  const drawerOpen = false;
  const [forceChangePassword, setForceChangePassword] = useState(false);
  const [aiChatOpen, setAiChatOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState('');
  const [chatConfig, setChatConfig] = useState(null);
  const [chatPrompts, setChatPrompts] = useState([]);
  const chatBoxRef = useRef(null);
  const [referenceDocs, setReferenceDocs] = useState([]);
  const [referenceDocsView, setReferenceDocsView] = useState('icon');
  const [referenceDocsPage, setReferenceDocsPage] = useState(1);
  const [referenceDocsPerPage] = useState(10);
  const [trashVisible, setTrashVisible] = useState(false);
  const [activitiesPage, setActivitiesPage] = useState(1);
  const [activitiesPerPage] = useState(20);
  const [videoResources, setVideoResources] = useState([]);
  const [videoResourcesLoading, setVideoResourcesLoading] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [usage, setUsage] = useState({ used: 0, quota: 1 });
  const [usageLoading, setUsageLoading] = useState(false);

  useEffect(() => {
    if (location.state && (location.state.mustChangePassword || location.state.must_change_password)) {
      setForceChangePassword(true);
      setChangePasswordOpen(true);
    }
  }, [location.state]);

  // Fetch chat config, history, and prompts when AI Chat view is active
  useEffect(() => {
    if (view === 'ai-chat' && user && token) {
      setChatError('');
      setChatLoading(true);
      Promise.all([
        axios.get('/drive/api/ai/chat/config'),
        axios.get('/drive/api/ai/chat/history?limit=30'),
        axios.get('/drive/api/ai/chat/prompts')
      ])
        .then(([configRes, histRes, promptsRes]) => {
          setChatConfig(configRes.data);
          setChatHistory(histRes.data.history || []);
          setChatPrompts(promptsRes.data.prompts || []);
        })
        .catch(() => setChatError('Failed to load chat history.'))
        .finally(() => setChatLoading(false));
    }
  }, [view, user, token]);

  // Scroll to bottom on new message
  useEffect(() => {
    if (chatBoxRef.current && view === 'ai-chat') {
      chatBoxRef.current.scrollTop = chatBoxRef.current.scrollHeight;
    }
  }, [chatHistory, view]);

  // Fetch initial data when user is authenticated (only once)
  useEffect(() => {
    if (user && token && !dataInitialized) {
      setDataInitialized(true);
      
      // Fetch reference docs
      axios.get('/drive/api/reference-docs', { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setReferenceDocs(res.data.files || []))
        .catch(() => setReferenceDocs([]));
      
      // Fetch video resources
      fetchVideoResources();
      
      // Fetch drive data
      fetchUploadedFiles();
      fetchTrashFiles();
      fetchSqlBackups();
      fetchUsage();
    }
  }, [user, token, dataInitialized]);

  const handleLogout = () => {
    // Clear data and reset initialization flag
    setReferenceDocs([]);
    setVideoResources([]);
    setUploadedFiles([]);
    setTrashFiles([]);
    setSqlBackups([]);
    setDataInitialized(false);
    logout();
    navigate('/login');
  };

  const handlePasswordChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value
    });
  };

  const handleChangePassword = async () => {
    setError('');
    setSuccess('');

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters long');
      return;
    }

    if (passwordForm.currentPassword === passwordForm.newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);

    const result = await changePassword(
      passwordForm.currentPassword,
      passwordForm.newPassword
    );

    if (result.success) {
      setSuccess('Password changed successfully');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => {
        setChangePasswordOpen(false);
        setSuccess('');
        setForceChangePassword(false);
      }, 2000);
    } else {
      setError(result.error);
    }

    setLoading(false);
  };

  const handleClosePasswordDialog = () => {
    setChangePasswordOpen(false);
    setPasswordForm({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setError('');
    setSuccess('');
  };

  const fetchActivities = async () => {
    setActivitiesLoading(true);
    try {
      const response = await axios.get('/drive/api/auth/activities', { headers: { Authorization: `Bearer ${token}` } });
      setActivities(response.data.activities || []);
      setActivitiesPage(1); // Reset to first page when fetching new activities
    } catch (error) {
      console.error('Failed to fetch activities:', error);
      setActivities([]);
    } finally {
      setActivitiesLoading(false);
    }
  };

  const fetchUploadedFiles = async () => {
    try {
      const res = await axios.get('/drive/api/files', { headers: { Authorization: `Bearer ${token}` } });
      setUploadedFiles(res.data.files);
    } catch (err) {
      setUploadedFiles([]);
    }
  };

  const handleUploadFile = async () => {
    if (!uploadFile) return;
    // Check quota before upload
    if (usage.used + uploadFile.size > usage.quota) {
      setUploadError('Upload would exceed your storage quota.');
      return;
    }
    setUploading(true);
    setUploadError('');
    const formData = new FormData();
    formData.append('file', uploadFile);
    try {
      await axios.post('/drive/api/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setUploadDialogOpen(false);
      setUploadFile(null);
      fetchUploadedFiles();
      fetchUsage();
    } catch (err) {
      setUploadError(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fetchTrashFiles = async () => {
    try {
      const res = await axios.get('/drive/api/trash', { headers: { Authorization: `Bearer ${token}` } });
      setTrashFiles(res.data.files);
    } catch (err) {
      setTrashFiles([]);
    }
  };

  const handleDownloadFile = async (file, isTrash = false) => {
    try {
      // Stream via the browser's native download manager (real progress UI,
      // streams to disk instead of buffering the file in memory).
      await triggerBrowserDownload({
        type: isTrash ? 'trash' : 'file',
        filename: file,
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
  };

  const handleDeleteFile = async (file) => {
    try {
      await axios.post('/drive/api/trash', { file });
      fetchUploadedFiles();
      fetchTrashFiles();
      fetchUsage();
    } catch (err) {
      alert('Delete failed');
    }
  };

  const handleRestoreFile = async (file) => {
    try {
      await axios.post('/drive/api/trash/restore', { file });
      fetchUploadedFiles();
      fetchTrashFiles();
      fetchUsage();
    } catch (err) {
      alert('Restore failed');
    }
  };

  const handleDeleteForever = async (file) => {
    try {
      await axios.post('/drive/api/trash/delete', { file });
      fetchTrashFiles();
      fetchUsage();
    } catch (err) {
      alert('Permanent delete failed');
    }
  };

  const handleDeleteForeverClick = (file) => {
    setFileToDelete(file);
    setDeleteConfirmOpen(true);
  };
  const handleDeleteForeverConfirm = async () => {
    if (fileToDelete) {
      await handleDeleteForever(fileToDelete);
      setFileToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };
  const handleDeleteForeverCancel = () => {
    setFileToDelete(null);
    setDeleteConfirmOpen(false);
  };

  const handleDownloadConfirm = () => {
    if (!fileToDownload) return;
    
    switch (downloadType) {
      case 'file':
        handleDownloadFile(fileToDownload.file, fileToDownload.isTrash);
        break;
      case 'reference':
        handleDownloadReferenceDoc(fileToDownload);
        break;
      case 'sqlbackup':
        handleDownloadSqlBackup(fileToDownload);
        break;
      default:
        break;
    }
    
    setDownloadConfirmOpen(false);
    setFileToDownload(null);
    setDownloadType('');
  };

  const handleDownloadCancel = () => {
    setDownloadConfirmOpen(false);
    setFileToDownload(null);
    setDownloadType('');
  };

  const handleDownloadFileClick = (file, isTrash = false) => {
    setFileToDownload({ file, isTrash });
    setDownloadType('file');
    setDownloadConfirmOpen(true);
  };

  const handleDownloadReferenceDocClick = (doc) => {
    setFileToDownload(doc);
    setDownloadType('reference');
    setDownloadConfirmOpen(true);
  };

  const handleDownloadSqlBackupClick = (file) => {
    setFileToDownload(file);
    setDownloadType('sqlbackup');
    setDownloadConfirmOpen(true);
  };

  const handleToggleTrash = () => {
    setTrashVisible(!trashVisible);
  };

  const handleRefreshSqlBackups = (e) => {
    fetchSqlBackups();
    return false;
  };

  const fetchSqlBackups = async () => {
    setSqlBackupsLoading(true);
    try {
      const res = await axios.get('/drive/api/sql-backups', { headers: { Authorization: `Bearer ${token}` } });
      setSqlBackups(res.data.backups);
    } catch (err) {
      setSqlBackups([]);
    } finally {
      setSqlBackupsLoading(false);
    }
  };

  const fetchVideoResources = async () => {
    setVideoResourcesLoading(true);
    try {
      const response = await fetch('/video_resources.json');
      const data = await response.json();
      setVideoResources(data.videoResources || []);
    } catch (error) {
      console.error('Failed to fetch video resources:', error);
      setVideoResources([]);
    } finally {
      setVideoResourcesLoading(false);
    }
  };

  const handleDownloadSqlBackup = async (file) => {
    try {
      // Uploads backups live in a dedicated subfolder, so they use their own
      // download-token type; database backups keep using the original one.
      await triggerBrowserDownload({
        type: file.category === 'uploads' ? 'sqlbackup_uploads' : 'sqlbackup',
        filename: file.name,
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
  };

  const handleSendChat = async () => {
    if (!chatInput.trim()) return;
    setChatLoading(true);
    setChatError('');
    try {
      // Optimistically add user message
      setChatHistory((h) => [...h, { role: 'user', message: chatInput, created_at: new Date().toISOString() }]);
      setChatInput('');
      const res = await axios.post('/drive/api/ai/chat/send', { message: chatInput });
      setChatHistory((h) => [...h, { role: 'ai', message: res.data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      setChatError('Failed to send message.');
    } finally {
      setChatLoading(false);
    }
  };

  const handlePromptClick = async (prompt) => {
    setChatInput('');
    setChatLoading(true);
    setChatError('');
    try {
      setChatHistory((h) => [...h, { role: 'user', message: prompt, created_at: new Date().toISOString() }]);
      const res = await axios.post('/drive/api/ai/chat/send', { message: prompt });
      setChatHistory((h) => [...h, { role: 'ai', message: res.data.reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      setChatError('Failed to send message.');
    } finally {
      setChatLoading(false);
    }
  };

  const handleDownloadReferenceDoc = async (doc) => {
    try {
      await triggerBrowserDownload({
        type: 'reference',
        filename: doc.name,
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (err) {
      console.error('Download failed:', err);
      alert('Download failed. Please try again.');
    }
  };

  const fetchUsage = async () => {
    setUsageLoading(true);
    try {
      const res = await axios.get('/drive/api/usage', { headers: { Authorization: `Bearer ${token}` } });
      setUsage(res.data);
    } catch (err) {
      setUsage({ used: 0, quota: 1 });
    } finally {
      setUsageLoading(false);
    }
  };

  useEffect(() => {
    if (user && token) {
      if (view === 'activities') {
        fetchActivities();
      }
      if (view === 'drive') {
        fetchUploadedFiles();
        fetchTrashFiles();
        fetchSqlBackups();
        fetchUsage();
      }
      if (view === 'resources') {
        // Resources data is already fetched in the initial useEffect
        // No additional fetching needed here
      }
    }
  }, [view, user, token]);



  const renderProfileContent = () => (
    <Grid container spacing={3}>
      {/* User Profile Card */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main', width: 48, height: 48 }}>
                <PersonIcon sx={{ fontSize: 32 }} />
              </Avatar>
              <Typography variant="h6">User Profile</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body1">
                {user?.username}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Email
              </Typography>
              <Typography variant="body1">
                {user?.email}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Authentication Provider
              </Typography>
              <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>
                {user?.auth_provider}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Member Since
              </Typography>
              <Typography variant="body1">
                {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Last Login
              </Typography>
              <Typography variant="body1">
                {user?.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
              </Typography>
            </Box>
            <Box sx={{ mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => setView('activities')}
              >
                View User Activity
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      {/* Quota Card */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', height: '100%' }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                Storage Quota
              </Typography>
              <PieChart width={90} height={90}>
                <Pie
                  data={[
                    { name: 'Used', value: usage.used },
                    { name: 'Remaining', value: Math.max(0, usage.quota - usage.used) }
                  ]}
                  cx={45}
                  cy={45}
                  innerRadius={28}
                  outerRadius={40}
                  startAngle={90}
                  endAngle={-270}
                  dataKey="value"
                >
                  <Cell key="used" fill="#e53935" />
                  <Cell key="remaining" fill="#43a047" />
                </Pie>
              </PieChart>
              <Box sx={{ mt: 1, fontSize: '1.1em', fontWeight: 500 }}>
                {usageLoading ? 'Loading...' : `${formatSize(usage.used)} / ${formatSize(usage.quota)}`}
              </Box>
              <Box sx={{ color: 'text.secondary', fontSize: '0.95em', mt: 0.5 }}>
                Used: <span style={{ color: '#e53935', fontWeight: 500 }}>{usageLoading ? '--' : formatSize(usage.used)}</span> &nbsp;|&nbsp; Remaining: <span style={{ color: '#43a047', fontWeight: 500 }}>{usageLoading ? '--' : formatSize(Math.max(0, usage.quota - usage.used))}</span>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>
      {/* Actions Card */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Account Actions
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Button
              fullWidth
              variant="outlined"
              startIcon={<LockIcon />}
              onClick={() => setChangePasswordOpen(true)}
              sx={{ mb: 2 }}
            >
              Change Password
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<EditIcon />}
              disabled
              sx={{ mb: 2 }}
            >
              Edit Profile (Coming Soon)
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              More features coming soon...
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderActivitiesContent = () => {
    // Calculate pagination
    const startIndex = (activitiesPage - 1) * activitiesPerPage;
    const endIndex = startIndex + activitiesPerPage;
    const paginatedActivities = activities.slice(startIndex, endIndex);
    const totalPages = Math.ceil(activities.length / activitiesPerPage);
    


    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={() => setView('profile')} sx={{ mr: 1 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6">User Activity</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          
          {activitiesLoading ? (
            <Typography>Loading activities...</Typography>
          ) : activities.length === 0 ? (
            <Typography color="text.secondary">No activities found.</Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: 'bold', py: 0.5 }}>Action</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: 'bold', py: 0.5 }}>Details</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: 'bold', py: 0.5 }}>IP Address</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '9px', fontWeight: 'bold', py: 0.5 }}>Date & Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedActivities.map((activity) => (
                      <TableRow key={activity.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              textTransform: 'capitalize',
                              fontWeight: 'bold',
                              fontFamily: 'monospace',
                              fontSize: '9px',
                              lineHeight: 1.2,
                              color: activity.action === 'login' ? 'success.main' : 
                                     activity.action === 'logout' ? 'warning.main' : 'primary.main'
                            }}
                          >
                            {activity.action.replace('_', ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', lineHeight: 1.2 }}>
                            {activity.details}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', lineHeight: 1.2 }}>
                            {activity.ip_address || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.5 }}>
                          <Typography sx={{ fontFamily: 'monospace', fontSize: '9px', lineHeight: 1.2 }}>
                            {new Date(activity.created_at).toLocaleString()}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {/* Pagination */}
              {activities.length > 0 && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2, 
                  mb: 1,
                  p: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'background.paper'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Page {activitiesPage} of {totalPages} ({activities.length} total activities)
                    </Typography>
                    <Pagination
                      count={Math.max(1, totalPages)}
                      page={activitiesPage}
                      onChange={(event, value) => setActivitiesPage(value)}
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderAIChatContent = () => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PsychologyIcon sx={{ mr: 1, color: muiTheme.palette.primary.main, fontSize: 28 }} />
          <Typography variant="h6">AI Chat</Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ height: '60vh', display: 'flex', flexDirection: 'column' }}>
          {/* Chat Messages */}
          <Box 
            ref={chatBoxRef} 
            sx={{ 
              flexGrow: 1, 
              overflowY: 'auto', 
              mb: 2, 
              pr: 1,
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              bgcolor: 'background.paper'
            }}
          >
            {chatHistory.length === 0 && (
              <Box sx={{ mb: 2, textAlign: 'center' }}>
                <Alert 
                  severity="info" 
                  sx={{ 
                    textAlign: 'left',
                    '& .MuiAlert-message': {
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    🤖 Sri Lankan Microfinance Expert
                  </Typography>
                  <Typography variant="body2">
                    I'm specialized in Sri Lankan microfinance field. All my responses are tailored to Sri Lanka and Southeast Asian context, including local regulations, practices, and cultural considerations.
                  </Typography>
                </Alert>
              </Box>
            )}
            {chatLoading && chatHistory.length === 0 && (
              <Typography color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>Loading chat...</Typography>
            )}
            {chatError && (
              <Alert severity="error" sx={{ mb: 1 }}>{chatError}</Alert>
            )}
            {chatHistory.map((msg, idx) => (
              <Box key={(msg.created_at || idx) + '-' + idx} sx={{ mb: 3, textAlign: msg.role === 'user' ? 'right' : 'left' }}>
                <Box
                  sx={{
                    display: 'inline-block',
                    bgcolor: msg.role === 'user' 
                      ? 'primary.light' 
                      : mode === 'dark' 
                        ? 'grey.800' 
                        : 'grey.200',
                    color: msg.role === 'user' 
                      ? 'primary.contrastText' 
                      : mode === 'dark' 
                        ? 'grey.100' 
                        : 'text.primary',
                    px: 2,
                    py: 1,
                    borderRadius: 2,
                    maxWidth: '80%',
                    wordBreak: 'break-word',
                    fontSize: '0.98rem',
                    border: msg.role === 'ai' && mode === 'dark' 
                      ? '1px solid' 
                      : 'none',
                    borderColor: msg.role === 'ai' && mode === 'dark' 
                      ? 'grey.700' 
                      : 'transparent',
                  }}
                >
                  {msg.role === 'ai' ? (
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // Custom styling for markdown elements
                        p: ({ children }) => <Box component="p" sx={{ margin: '0.5em 0', '&:first-of-type': { marginTop: 0 }, '&:last-of-type': { marginBottom: 0 } }}>{children}</Box>,
                        h1: ({ children }) => <Box component="h1" sx={{ fontSize: '1.5em', fontWeight: 'bold', margin: '0.5em 0', color: 'inherit' }}>{children}</Box>,
                        h2: ({ children }) => <Box component="h2" sx={{ fontSize: '1.3em', fontWeight: 'bold', margin: '0.5em 0', color: 'inherit' }}>{children}</Box>,
                        h3: ({ children }) => <Box component="h3" sx={{ fontSize: '1.1em', fontWeight: 'bold', margin: '0.5em 0', color: 'inherit' }}>{children}</Box>,
                        h4: ({ children }) => <Box component="h4" sx={{ fontSize: '1em', fontWeight: 'bold', margin: '0.5em 0', color: 'inherit' }}>{children}</Box>,
                        h5: ({ children }) => <Box component="h5" sx={{ fontSize: '0.9em', fontWeight: 'bold', margin: '0.5em 0', color: 'inherit' }}>{children}</Box>,
                        h6: ({ children }) => <Box component="h6" sx={{ fontSize: '0.8em', fontWeight: 'bold', margin: '0.5em 0', color: 'inherit' }}>{children}</Box>,
                        ul: ({ children }) => <Box component="ul" sx={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>{children}</Box>,
                        ol: ({ children }) => <Box component="ol" sx={{ margin: '0.5em 0', paddingLeft: '1.5em' }}>{children}</Box>,
                        li: ({ children }) => <Box component="li" sx={{ margin: '0.2em 0' }}>{children}</Box>,
                        blockquote: ({ children }) => <Box component="blockquote" sx={{ borderLeft: '3px solid', borderColor: 'primary.main', paddingLeft: '1em', margin: '0.5em 0', fontStyle: 'italic' }}>{children}</Box>,
                        code: ({ children, className }) => {
                          const isInline = !className;
                          return isInline ? (
                            <Box component="code" sx={{ 
                              backgroundColor: 'action.hover', 
                              padding: '0.2em 0.4em', 
                              borderRadius: '0.3em', 
                              fontSize: '0.9em',
                              fontFamily: 'monospace'
                            }}>
                              {children}
                            </Box>
                          ) : (
                            <Box component="pre" sx={{ 
                              backgroundColor: 'action.hover', 
                              padding: '1em', 
                              borderRadius: '0.5em', 
                              overflow: 'auto',
                              margin: '0.5em 0',
                              fontSize: '0.9em',
                              fontFamily: 'monospace'
                            }}>
                              <code>{children}</code>
                            </Box>
                          );
                        },
                        a: ({ children, href }) => <Box component="a" href={href} target="_blank" rel="noopener noreferrer" sx={{ color: 'primary.main', textDecoration: 'underline', '&:hover': { textDecoration: 'none' } }}>{children}</Box>,
                        strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 'bold' }}>{children}</Box>,
                        em: ({ children }) => <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>,
                        hr: () => <Box component="hr" sx={{ border: 'none', borderTop: '1px solid', borderColor: 'divider', margin: '1em 0' }} />,
                        table: ({ children }) => <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%', margin: '0.5em 0' }}>{children}</Box>,
                        thead: ({ children }) => <Box component="thead">{children}</Box>,
                        tbody: ({ children }) => <Box component="tbody">{children}</Box>,
                        tr: ({ children }) => <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>{children}</Box>,
                        th: ({ children }) => <Box component="th" sx={{ padding: '0.5em', textAlign: 'left', fontWeight: 'bold', borderBottom: '2px solid', borderColor: 'divider' }}>{children}</Box>,
                        td: ({ children }) => <Box component="td" sx={{ padding: '0.5em', borderBottom: '1px solid', borderColor: 'divider' }}>{children}</Box>,
                      }}
                    >
                      {msg.message}
                    </ReactMarkdown>
                  ) : (
                    msg.message
                  )}
                </Box>
                {msg.role === 'user' ? (
                  <Typography 
                    variant="caption" 
                    color="text.secondary" 
                    sx={{ 
                      display: 'block',
                      textAlign: 'right',
                      mt: 0.5,
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}
                  >
                    You
                  </Typography>
                ) : (
                  <Box sx={{ 
                    display: 'block',
                    textAlign: 'right',
                    mt: 0.5
                  }}>
                    <Typography variant="caption" sx={{ 
                      display: 'block', 
                      lineHeight: 1.2, 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: '#ba68c8'
                    }}>
                      RAJIDA
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      display: 'block', 
                      lineHeight: 1.2, 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: '#ba68c8'
                    }}>
                      Microfinance
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      display: 'block', 
                      lineHeight: 1.2, 
                      fontSize: '0.7rem',
                      fontWeight: 500,
                      color: '#ba68c8'
                    }}>
                      AI
                    </Typography>
                  </Box>
                )}
              </Box>
            ))}
          </Box>

          {/* Prompts */}
          {chatPrompts.length > 0 && (
            <Box sx={{ mb: 2, width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Quick Prompts:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {chatPrompts.map((prompt, idx) => (
                  <Chip
                    key={idx}
                    color="success"
                    variant="filled"
                    sx={{
                      bgcolor: 'success.light',
                      color: 'white',
                      fontWeight: 500,
                      fontSize: '0.8em',
                      height: 24,
                      maxWidth: 'fit-content',
                      justifyContent: 'center',
                      boxShadow: 1,
                      px: 1,
                      py: 0.5,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'success.main',
                      },
                    }}
                    onClick={() => handlePromptClick(prompt)}
                    disabled={chatLoading}
                    label={prompt}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Input Area */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              placeholder="Type your message..."
              value={chatInput}
              onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !chatLoading) handleSendChat(); }}
              disabled={chatLoading}
              multiline
              maxRows={3}
            />
            <IconButton 
              onClick={handleSendChat} 
              disabled={chatLoading || !chatInput.trim()}
              sx={{ 
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': {
                  bgcolor: 'primary.dark',
                },
                '&:disabled': {
                  bgcolor: 'action.disabledBackground',
                  color: 'action.disabled',
                },
                width: 56,
                height: 56
              }}
            >
              {chatLoading ? (
                <Box
                  sx={{
                    animation: `${spin} 1s linear infinite`,
                    display: 'inline-block'
                  }}
                >
                  <RefreshIcon />
                </Box>
              ) : (
                <SendIcon />
              )}
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderResourcesContent = () => (
    <Grid container spacing={3}>
      {/* Reference Documents Section */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <DescriptionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">Reference Documents</Typography>
              </Box>
              <ToggleButtonGroup
                value={referenceDocsView}
                exclusive
                onChange={(_, v) => {
                  if (v) {
                    setReferenceDocsView(v);
                    setReferenceDocsPage(1); // Reset to first page when view changes
                  }
                }}
                size="small"
                sx={{ ml: 2 }}
              >
                <ToggleButton value="list" aria-label="List view">
                  <DescriptionIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="icon" aria-label="Large icon view">
                  <PictureAsPdfIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {referenceDocs.length === 0 ? (
              <Typography color="text.secondary">No reference documents found.</Typography>
            ) : (() => {
              const sortedDocs = [...referenceDocs].sort((a, b) => a.name.localeCompare(b.name));
              const totalPages = Math.ceil(sortedDocs.length / referenceDocsPerPage);
              const startIndex = (referenceDocsPage - 1) * referenceDocsPerPage;
              const endIndex = startIndex + referenceDocsPerPage;
              const currentPageDocs = sortedDocs.slice(startIndex, endIndex);

              return (
                <>
                  {referenceDocsView === 'list' ? (
                    <Box>
                      {currentPageDocs.map((doc, idx) => (
                        <Box key={doc.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <PictureAsPdfIcon color="error" sx={{ mr: 1 }} />
                          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                            <Tooltip title={doc.name} arrow>
                              <Typography variant="body2" sx={{ flexGrow: 1 }}>{doc.name}</Typography>
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {formatSize(doc.size)} &nbsp;|&nbsp; {doc.date ? new Date(doc.date).toLocaleString() : ''}
                            </Typography>
                          </Box>
                          <IconButton
                            onClick={() => handleDownloadReferenceDocClick(doc)}
                            size="small"
                            color="primary"
                            sx={{ ml: 1 }}
                          >
                            <Tooltip title="Download">
                              <DownloadIcon />
                            </Tooltip>
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
                      {currentPageDocs.map((doc, idx) => (
                        <Tooltip title={doc.name} arrow>
                          <Box key={doc.name} sx={{ 
                            width: 110, 
                            textAlign: 'center', 
                            p: 1, 
                            borderRadius: 2, 
                            bgcolor: mode === 'dark' ? 'grey.800' : 'grey.100', 
                            boxShadow: 1,
                            border: mode === 'dark' ? '1px solid' : 'none',
                            borderColor: mode === 'dark' ? 'grey.700' : 'transparent'
                          }}>
                            <Box
                              sx={{ cursor: 'pointer' }}
                              onClick={() => handleDownloadReferenceDocClick(doc)}
                            >
                              <PictureAsPdfIcon color="error" sx={{ fontSize: 48, mb: 1 }} />
                              <Typography variant="body2" sx={{ 
                                fontWeight: 500, 
                                mb: 0.5, 
                                wordBreak: 'break-all',
                                color: mode === 'dark' ? 'grey.100' : 'text.primary'
                              }}>{truncateMiddle(doc.name, 25)}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {formatSize(doc.size)}
                              </Typography>
                            </Box>
                          </Box>
                        </Tooltip>
                      ))}
                    </Box>
                  )}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination
                        count={totalPages}
                        page={referenceDocsPage}
                        onChange={(event, value) => setReferenceDocsPage(value)}
                        size="small"
                        color="primary"
                      />
                    </Box>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
      </Grid>

      {/* Video Resources Section */}
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <YouTubeIcon color="error" sx={{ mr: 1 }} />
              <Typography variant="h6">Video Resources</Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
            {videoResourcesLoading ? (
              <Typography color="text.secondary">Loading video resources...</Typography>
            ) : videoResources.length === 0 ? (
              <Typography color="text.secondary">No video resources found.</Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'flex-start' }}>
                {videoResources.map((vid, idx) => {
                  // Extract YouTube video ID from URL
                  const getYouTubeThumbnail = (url) => {
                    let videoId = '';
                    if (url.includes('youtube.com/watch?v=')) {
                      videoId = url.split('v=')[1]?.split('&')[0];
                      if (videoId) {
                        return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
                      }
                    }
                    // For playlists, channels, or invalid/missing videoId, use a generic placeholder
                    return '/static/imgs/youtube-placeholder.png';
                  };

                  const thumbnailUrl = getYouTubeThumbnail(vid.url);
                  
                  return (
                    <Tooltip title={vid.title} arrow key={vid.url}>
                      <Box 
                        sx={{ 
                          width: 200, 
                          textAlign: 'center', 
                          p: 1, 
                          borderRadius: 2, 
                          bgcolor: 'background.paper', 
                          border: 1,
                          borderColor: 'divider',
                          boxShadow: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            boxShadow: 3,
                            bgcolor: 'action.hover'
                          }
                        }}
                        onClick={() => window.open(vid.url, '_blank', 'noopener,noreferrer')}
                      >
                        <Box sx={{ position: 'relative', mb: 1 }}>
                          {thumbnailUrl ? (
                            <Box sx={{ position: 'relative' }}>
                              <img 
                                src={thumbnailUrl} 
                                alt={vid.title}
                                style={{ 
                                  width: '100%', 
                                  height: 120, 
                                  objectFit: 'cover',
                                  borderRadius: 8
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                              {/* Gradient overlay for better text readability */}
                              <Box 
                                sx={{
                                  position: 'absolute',
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  height: '40%',
                                  background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
                                  borderRadius: '0 0 8px 8px',
                                  pointerEvents: 'none'
                                }}
                              />
                            </Box>
                          ) : null}
                          <Box 
                            sx={{ 
                              display: thumbnailUrl ? 'none' : 'flex',
                              width: '100%', 
                              height: 120, 
                              bgcolor: 'action.disabledBackground',
                              borderRadius: 8,
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <YouTubeIcon sx={{ fontSize: 48, color: 'action.disabled' }} />
                          </Box>
                          <Box 
                            sx={{ 
                              position: 'absolute', 
                              top: 8, 
                              right: 8,
                              bgcolor: 'rgba(0,0,0,0.7)',
                              color: 'white',
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <YouTubeIcon sx={{ fontSize: 16 }} />
                          </Box>
                        </Box>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontWeight: 500, 
                            mb: 0.5, 
                            wordBreak: 'break-word',
                            lineHeight: 1.2,
                            height: 40,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            color: 'text.primary'
                          }}
                        >
                          {vid.title}
                        </Typography>
                        {vid.description && (
                          <Typography 
                            variant="caption" 
                            color="text.secondary" 
                            sx={{ 
                              display: 'block',
                              lineHeight: 1.2,
                              height: 32,
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {vid.description}
                          </Typography>
                        )}
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderDriveContent = () => (
    <>
      {/* Quota Usage Card */}
      <Grid container spacing={3} sx={{ mb: 2 }}>
        <Grid item xs={12}>
          <Card sx={{ width: '100%', mb: 1, bgcolor: 'background.paper', boxShadow: 2, px: 2 }}>
            <CardContent sx={{ p: 1.2, px: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', mb: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BarChartIcon sx={{ fontSize: 32, mr: 1, color: muiTheme.palette.primary.main }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Storage Usage
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120, textAlign: 'right' }}>
                  {usageLoading ? 'Loading...' : `${formatSize(usage.used)} / ${formatSize(usage.quota)}`}
                </Typography>
              </Box>
              <Box sx={{ mt: 0.5, mb: 0.2 }}>
                <LinearProgress 
                  variant="determinate" 
                  value={usageLoading ? 0 : Math.min(100, (usage.used / usage.quota) * 100)} 
                  sx={{ height: 7, borderRadius: 4, backgroundColor: '#43a047',
                    '& .MuiLinearProgress-bar': { backgroundColor: '#e53935' } }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.92em', color: 'text.secondary', mt: 0.2 }}>
                <span>Used: <span style={{ color: '#e53935', fontWeight: 500 }}>{usageLoading ? '--' : formatSize(usage.used)}</span></span>
                <span>Remaining: <span style={{ color: '#43a047', fontWeight: 500 }}>{usageLoading ? '--' : formatSize(Math.max(0, usage.quota - usage.used))}</span></span>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Main Drive Content */}
      <Grid container spacing={3}>
        {/* Uploaded Files Section */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <CloudUploadIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Uploaded Files</Typography>
                </Box>
                <Tooltip title="Add file to upload" arrow>
                  <IconButton 
                    onClick={() => setUploadDialogOpen(true)}
                    sx={{
                      bgcolor: 'success.main',
                      color: 'white',
                      width: 40,
                      height: 40,
                      '&:hover': {
                        bgcolor: 'success.dark',
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: 24 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {uploadedFiles.length === 0 ? (
                <Typography color="text.secondary">No files uploaded yet.</Typography>
              ) : (
                <Box>
                  {[...uploadedFiles]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((file) => {
                    const fileIcon = getFileIcon(file.name);
                    const IconComponent = fileIcon.icon;
                    
                    return (
                      <Box key={file.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <IconComponent sx={{ mr: 1, color: fileIcon.color, fontSize: 24 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Tooltip title={file.name} arrow>
                            <Typography variant="body1" noWrap sx={{ maxWidth: 400, display: 'block' }}>{truncateMiddle(file.name)}</Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {formatSize(file.size)} &nbsp;|&nbsp; {new Date(file.date).toLocaleString()}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => handleDownloadFileClick(file.name)} size="small"><DownloadIcon /></IconButton>
                        <IconButton onClick={() => handleDeleteFile(file.name)} size="small"><DeleteIcon /></IconButton>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
          {/* Trash Icon with Count Bubble */}
          {trashFiles.length > 0 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ position: 'relative', cursor: 'pointer' }} onClick={handleToggleTrash}>
                <IconButton
                  sx={{
                    bgcolor: 'rgba(244,67,54,0.1)',
                    border: '2px solid rgba(244,67,54,0.3)',
                    '&:hover': {
                      bgcolor: 'rgba(244,67,54,0.2)',
                      border: '2px solid rgba(244,67,54,0.5)',
                    },
                    width: 48,
                    height: 48,
                  }}
                >
                  <RestoreFromTrashIcon sx={{ fontSize: 24, color: 'error.main' }} />
                </IconButton>
                <Box
                  sx={{
                    position: 'absolute',
                    top: -6,
                    right: -6,
                    bgcolor: 'error.main',
                    color: 'white',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    border: '2px solid white',
                  }}
                >
                  {trashFiles.length}
                </Box>
              </Box>
            </Box>
          )}

          {/* Trash Content (toggleable) */}
          {trashVisible && trashFiles.length > 0 && (
            <Card sx={{ mt: 2, backgroundColor: 'rgba(244,67,54,0.08)', backgroundImage: 'none' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <RestoreFromTrashIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Trash</Typography>
                  </Box>
                  <IconButton
                    onClick={handleToggleTrash}
                    size="small"
                    color="primary"
                  >
                    <Tooltip title="Hide Trash">
                      <RestoreFromTrashIcon />
                    </Tooltip>
                  </IconButton>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Box>
                  {[...trashFiles]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((file) => {
                    const fileIcon = getFileIcon(file.name);
                    const IconComponent = fileIcon.icon;
                    
                    return (
                      <Box key={file.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <IconComponent sx={{ mr: 1, color: fileIcon.color, fontSize: 24 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Tooltip title={file.name} arrow>
                            <Typography variant="body1" noWrap sx={{ maxWidth: 220, display: 'block' }}>{truncateMiddle(file.name)}</Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary">
                            {formatSize(file.size)} &nbsp;|&nbsp; {new Date(file.date).toLocaleString()}
                          </Typography>
                        </Box>
                        <IconButton onClick={() => handleRestoreFile(file.name)} size="small"><RestoreFromTrashIcon /></IconButton>
                        <IconButton onClick={() => handleDeleteForeverClick(file.name)} size="small" color="error"><DeleteForeverIcon /></IconButton>
                      </Box>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>
        {/* SQL Backups Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ backgroundColor: 'rgba(33,150,243,0.08)', backgroundImage: 'none' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <StorageIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">System Backups</Typography>
                </Box>
                <Box component="div" onClick={(e) => e.stopPropagation()}>
                  <IconButton
                    onClick={handleRefreshSqlBackups}
                    size="small"
                    color="primary"
                    sx={{ ml: 1 }}
                    disabled={sqlBackupsLoading}
                    type="button"
                  >
                    <Tooltip title="Refresh Backups">
                      {sqlBackupsLoading ? (
                        <Box sx={{ animation: `${spin} 1s linear infinite` }}>
                          <RefreshIcon />
                        </Box>
                      ) : (
                        <RefreshIcon />
                      )}
                    </Tooltip>
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              {sqlBackupsLoading ? (
                <Typography color="text.secondary">Loading backups...</Typography>
              ) : sqlBackups.length === 0 ? (
                <Typography color="text.secondary">No SQL backup files found.</Typography>
              ) : (
                <Box>
                  {[...sqlBackups]
                    .sort((a, b) => new Date(b.date) - new Date(a.date))
                    .map((file) => {
                    const fileIcon = getFileIcon(file.name);
                    const IconComponent = fileIcon.icon;
                    
                    return (
                      <Box key={file.name} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <IconComponent sx={{ mr: 1, color: fileIcon.color, fontSize: 20 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Tooltip title={file.name} arrow>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 400, display: 'block' }}>{truncateMiddle(file.name)}</Typography>
                          </Tooltip>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {file.category === 'uploads' ? 'Uploads backup' : 'Database backup'} &nbsp;|&nbsp; {formatSize(file.size)} &nbsp;|&nbsp; {new Date(file.date).toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

      </Grid>
      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Upload File</DialogTitle>
        <DialogContent sx={{ minWidth: 350 }}>
          <label htmlFor="file-upload-input">
            <input
              id="file-upload-input"
              type="file"
              style={{ display: 'none' }}
              onChange={e => setUploadFile(e.target.files[0])}
            />
            <Button
              variant="outlined"
              component="span"
              startIcon={<FileOpenIcon />}
              size="medium"
              sx={{ mb: 1, maxWidth: 200 }}
            >
              Choose File
            </Button>
          </label>
          {uploadFile && (
            <Tooltip title={uploadFile.name} arrow>
              <Typography variant="body2" noWrap sx={{ maxWidth: 200, mt: 0.5 }}>
                {truncateMiddle(uploadFile.name, 64)}
              </Typography>
            </Tooltip>
          )}
          {uploadError && <Alert severity="error" sx={{ mt: 2 }}>{uploadError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)} disabled={uploading}>Cancel</Button>
          <Button
            onClick={handleUploadFile}
            variant="contained"
            color="success"
            disabled={uploading || (usage.used >= usage.quota) || (uploadFile && (usage.used + uploadFile.size > usage.quota))}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const menuItems = [
    {
      id: 'drive',
      label: 'Files',
      icon: <SecurityIcon sx={{ fontSize: 32, width: 32, height: 32 }} />,
      onClick: () => setView('drive'),
      selected: view === 'drive',
      tooltip: 'Secure File Management & Uploads'
    },
    {
      id: 'resources',
      label: 'Resources',
      icon: <LibraryBooksIcon sx={{ fontSize: 32, width: 32, height: 32 }} />,
      onClick: () => setView('resources'),
      selected: view === 'resources',
      tooltip: 'Reference Documents & Video Resources'
    },
    {
      id: 'ai-chat',
      label: 'AI Chat',
      icon: <PsychologyIcon sx={{ fontSize: 28, width: 28, height: 28 }} />,
      onClick: () => setView('ai-chat'),
      selected: view === 'ai-chat',
      tooltip: 'RAJIDA Microfinance AI Assistant'
    },
    {
      id: 'profile',
      label: 'User Profile',
      icon: <AccountCircleIcon sx={{ fontSize: 28, width: 28, height: 28 }} />,
      onClick: () => setView('profile'),
      selected: view === 'profile',
      tooltip: 'Account Settings & Activities'
    },
  ];

  const drawer = (
    <Box>
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {APP_NAME}
          </Typography>
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.id} disablePadding>
            <ListItemButton 
              onClick={item.onClick}
              selected={item.selected}
              sx={{
                minHeight: 56,
                justifyContent: 'initial',
                px: 2.5,
                py: 1.5,
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                  mr: 2.5,
                  justifyContent: 'center',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Tooltip title={item.tooltip} arrow placement="right">
                  {item.icon}
                </Tooltip>
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
                      <ListItemButton 
              onClick={handleLogout}
              sx={{
                minHeight: 56,
                justifyContent: 'initial',
                px: 2.5,
                py: 1.5,
              }}
            >
            <ListItemIcon
              sx={{
                minWidth: 36,
                mr: 2.5,
                justifyContent: 'center',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Tooltip title="Sign Out" arrow placement="right">
                <LogoutIcon sx={{ fontSize: 28, width: 28, height: 28 }} />
              </Tooltip>
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Navigation Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_COLLAPSED_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_COLLAPSED_WIDTH,
            boxSizing: 'border-box',
            overflow: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          minHeight: '100vh'
        }}
      >
        {/* App Bar */}
        <AppBar 
          position="fixed" 
          sx={{ 
            zIndex: (theme) => theme.zIndex.drawer + 1,
            backgroundColor: 'background.paper',
            color: 'text.primary',
            boxShadow: 1
          }}
        >
          <Toolbar>
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
              <img src={RajidaLogo} alt="Rajida Logo" style={{ height: 32, marginRight: 12 }} />
              <Typography variant="h6" component="div">
                {view === 'activities' ? 'User Activity' : 
                 view === 'drive' ? 'Drive' : 
                 view === 'ai-chat' ? 'Microfinance AI' : 
                 view === 'resources' ? 'Resources' : 
                 'User Profile'}
              </Typography>
              {view === 'drive' && user?.username && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'normal',
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    ml: 2,
                    fontSize: '0.9rem',
                    display: 'inline-block',
                  }}
                >
                  {user.username}
                </Typography>
              )}
              {user?.name && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 'normal',
                    bgcolor: 'action.hover',
                    color: 'text.primary',
                    px: 2,
                    py: 0.5,
                    borderRadius: 1,
                    ml: 3,
                    fontSize: '0.9rem',
                    display: 'inline-block',
                  }}
                >
                  {user.name}
                </Typography>
              )}
            </Box>
            <Button
              href="https://support.rajida.com/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<HelpOutlineIcon />}
              variant="outlined"
              size="small"
              sx={{ ml: 1, mr: 1, textTransform: 'none', fontWeight: 500 }}
            >
              Support
            </Button>

            <IconButton onClick={toggleTheme} color="inherit">
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </IconButton>
          </Toolbar>
        </AppBar>
        
        <Toolbar />
        
        {/* Page Content */}
        <Box sx={{ p: 3 }}>
          {view === 'profile' ? renderProfileContent() : 
           view === 'activities' ? renderActivitiesContent() : 
           view === 'ai-chat' ? renderAIChatContent() : 
           view === 'resources' ? renderResourcesContent() : 
           renderDriveContent()}
        </Box>
        <Box sx={{ width: '100%', textAlign: 'center', py: 2, color: 'text.secondary', fontSize: '0.8em', borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          Copyright © 2025 Rajida Holdings (Pvt) Ltd. All Rights Reserved.
        </Box>

        {/* Change Password Dialog */}
        <Dialog open={changePasswordOpen} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
          <DialogTitle>Change Password</DialogTitle>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="currentPassword"
              label="Current Password"
              type="password"
              value={passwordForm.currentPassword}
              onChange={handlePasswordChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="newPassword"
              label="New Password"
              type="password"
              value={passwordForm.newPassword}
              onChange={handlePasswordChange}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm New Password"
              type="password"
              value={passwordForm.confirmPassword}
              onChange={handlePasswordChange}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClosePasswordDialog}>Cancel</Button>
            <Button 
              onClick={handleChangePassword} 
              variant="contained"
              disabled={loading}
            >
              {loading ? 'Changing...' : 'Change Password'}
            </Button>
          </DialogActions>
        </Dialog>
        {/* Delete Forever Confirmation Dialog */}
        <Dialog open={deleteConfirmOpen} onClose={handleDeleteForeverCancel}>
          <DialogTitle>Confirm Permanent Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to permanently delete this file? This action cannot be undone.</Typography>
            {fileToDelete && (
              <Typography sx={{ mt: 2, fontWeight: 'bold' }}>{fileToDelete}</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDeleteForeverCancel}>Cancel</Button>
            <Button onClick={handleDeleteForeverConfirm} color="error" variant="contained">Delete Permanently</Button>
          </DialogActions>
        </Dialog>
        {/* Download Confirmation Dialog */}
        <Dialog open={downloadConfirmOpen} onClose={handleDownloadCancel}>
          <DialogTitle>Confirm Download</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to download this file?</Typography>
            {fileToDownload && (
              <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
                {downloadType === 'file' ? fileToDownload.file : 
                 downloadType === 'reference' ? fileToDownload.name : 
                 downloadType === 'sqlbackup' ? fileToDownload.name : 'Unknown file'}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleDownloadCancel}>Cancel</Button>
            <Button onClick={handleDownloadConfirm} color="primary" variant="contained">Download</Button>
          </DialogActions>
        </Dialog>
        {forceChangePassword && !changePasswordOpen && (
          <Dialog open={true}>
            <DialogTitle>Password Change Required</DialogTitle>
            <DialogContent>
              <Typography>You must change your password before using the application.</Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setChangePasswordOpen(true)} variant="contained">Change Password</Button>
            </DialogActions>
          </Dialog>
        )}
      </Box>

    </Box>
  );
};

export default Dashboard; 